import { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  DialogActions,
  DialogContent,
  Grid,
  MenuItem,
  TextField,
  Typography
} from "@mui/material";
import { useTranslation } from "react-i18next";
import DialogComponent from "../../Dialog/Dialog";
import ActionButton from "../../Button/ActionButton";
import useProfessionalProfile from "../../../hooks/useProfessionalProfile";
import {
  PhoneNumberInput,
  serverMessage
} from "../../../services/professionals";
import {
  DEFAULT_COUNTRY_CODE,
  countryOptions,
  isValidLocalPhoneNumber
} from "../../../utils/countries";
import { outlinedFieldSx } from "../../../utils/fieldStyles";
import { semanticColors } from "../../../utils/colors";
import { typography } from "../../../utils/typography";

// Adds a personal phone.
//
// Unlike the CMD number, which has to receive an SMS, a landline is valid here —
// so the only check is that the number is dialable in the country picked. The
// country travels as an ISO code: the API rejects dialling codes outright, since
// "+351" does not identify a country on its own.

interface Props {
  open: boolean;
  /** Numbers already on file, to catch a duplicate before sending it. */
  existing: Array<{ countryPrefix?: string; number?: string }>;
  onClose: () => void;
  onAdded: () => void;
}

export default function AddPersonalPhoneDialog({
  open,
  existing,
  onClose,
  onAdded
}: Props) {
  const { t } = useTranslation();
  const { addPersonalPhone } = useProfessionalProfile();

  const [phone, setPhone] = useState<PhoneNumberInput>({
    countryPrefix: DEFAULT_COUNTRY_CODE,
    number: ""
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setPhone({ countryPrefix: DEFAULT_COUNTRY_CODE, number: "" });
    setError(null);
  }, [open]);

  // The server compares in canonical form; digits alone is close enough to catch
  // the same number typed with different spacing.
  const digits = (value?: string) => (value ?? "").replace(/\D/g, "");
  const typed = digits(phone.number);

  const malformed =
    typed.length > 0 &&
    !isValidLocalPhoneNumber(phone.countryPrefix, phone.number);
  const duplicate = existing.some(
    entry =>
      digits(entry.number) === typed &&
      (entry.countryPrefix ?? DEFAULT_COUNTRY_CODE) === phone.countryPrefix
  );

  const fieldError = malformed
    ? t("professionalData.phoneInvalid")
    : duplicate && typed.length > 0
    ? t("contactLists.phoneDuplicate")
    : undefined;

  const canSubmit = typed.length > 0 && !fieldError && !saving;

  const onSubmit = useCallback(async () => {
    if (!canSubmit) return;
    setSaving(true);
    setError(null);
    try {
      // Stored as bare digits server side; sending them that way keeps what
      // was typed and what was saved the same.
      await addPersonalPhone({
        countryPrefix: phone.countryPrefix,
        number: typed
      });
      onAdded();
      onClose();
    } catch (err) {
      setError(serverMessage(err) ?? t("contactLists.phoneAddError"));
    } finally {
      setSaving(false);
    }
  }, [
    canSubmit,
    phone.countryPrefix,
    typed,
    addPersonalPhone,
    onAdded,
    onClose,
    t
  ]);

  return (
    <DialogComponent
      open={open}
      handleModal={onClose}
      headerTxt={t("contactLists.addPhoneTitle")}
      maxWidth="sm"
    >
      <DialogContent>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={5}>
            <TextField
              select
              fullWidth
              label={t("paymentConfigurationForm.phoneCountry")}
              value={phone.countryPrefix}
              onChange={event =>
                setPhone(prev => ({
                  ...prev,
                  countryPrefix: event.target.value
                }))
              }
              sx={outlinedFieldSx}
            >
              {countryOptions.map(option => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={7}>
            <TextField
              autoFocus
              fullWidth
              label={t("professionalData.phoneNumber")}
              value={phone.number}
              onChange={event =>
                setPhone(prev => ({ ...prev, number: event.target.value }))
              }
              error={Boolean(fieldError)}
              helperText={fieldError ?? ""}
              inputProps={{ inputMode: "tel" }}
              sx={outlinedFieldSx}
            />
          </Grid>
        </Grid>
        <Typography
          sx={{
            ...typography.body.s,
            color: semanticColors.text.secondary,
            mt: 1,
            mb: 2
          }}
        >
          {t("contactLists.phoneNote")}
        </Typography>
        {error && <Alert severity="error">{error}</Alert>}
      </DialogContent>
      <DialogActions>
        <Box display="flex" gap={1} p={1}>
          <ActionButton
            label={t("common.cancel")}
            onClick={onClose}
            variant="secondary"
          />
          <ActionButton
            label={t("contactLists.add")}
            onClick={onSubmit}
            loading={saving}
            disabled={!canSubmit}
          />
        </Box>
      </DialogActions>
    </DialogComponent>
  );
}
